import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import {
  Callout,
  Checklist,
  CodeBlock,
  FigureFrame,
  GuideToc,
  Mono,
  Section,
  Table,
  useTocScrollSpy,
} from './components/guide-primitives';

/**
 * AWS S3 Storage Provider — Setup & Migration Guide.
 *
 * Opened in a NEW TAB from the Storage Providers page so the administrator can
 * keep the Configure form open in the original tab and follow this side-by-side.
 * That is the whole point of the feature, so this page deliberately does NOT
 * assume it is the only tab and never navigates the opener.
 *
 * EVERYTHING HERE IS DERIVED FROM THE IMPLEMENTATION, NOT FROM GENERIC AWS DOCS:
 *
 * - The five configuration fields come from `PROVIDER_CONFIG_SCHEMA.s3` in
 *   `backend/src/ame/services/provider-config.service.ts`. There is no prefix,
 *   path, ACL or path-style field, so none is documented.
 * - The four IAM actions are derived from the six AWS SDK commands actually
 *   issued by `S3CompatibleStorageDriver`. Nothing else is requested.
 * - The connection test is `HeadBucketCommand` only — documented as such,
 *   including the consequence that a PASS does not prove write access.
 * - Migration statuses, the seven-step operator model and the twenty
 *   finalization gates are the real ones.
 *
 * If a field, permission or workflow is not in the product, it is not in this
 * guide.
 */

/* ------------------------------------------------------------------ *
 * Presentational primitives — shared with the Cloudflare R2 guide
 * ------------------------------------------------------------------ */

/**
 * Every figure on this page is a hand-drawn wireframe, never a captured
 * screenshot. The disclaimer is injected once here so no individual figure can
 * ship without it.
 */
const AWS_FIGURE_DISCLAIMER =
  'Illustrative diagram, not a live screenshot of the AWS Console. AWS changes its ' +
  'console wording and layout frequently — follow the navigation path described in the ' +
  'steps, and use this diagram to confirm you are on the right screen.';

function Figure(props: { n: number; caption: string; look: string; children: React.ReactNode }) {
  return <FigureFrame {...props} disclaimer={AWS_FIGURE_DISCLAIMER} />;
}

/* ------------------------------------------------------------------ *
 * Reference data — mirrors the implementation
 * ------------------------------------------------------------------ */

const PRODUCTION_POLICY = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "ServicePortalBucketLevelAccess",
      "Effect": "Allow",
      "Action": "s3:ListBucket",
      "Resource": "arn:aws:s3:::YOUR-BUCKET-NAME"
    },
    {
      "Sid": "ServicePortalObjectLevelAccess",
      "Effect": "Allow",
      "Action": [
        "s3:GetObject",
        "s3:PutObject",
        "s3:DeleteObject"
      ],
      "Resource": "arn:aws:s3:::YOUR-BUCKET-NAME/*"
    }
  ]
}`;

const TOC: Array<{ id: string; label: string }> = [
  { id: 'overview', label: 'Overview & architecture' },
  { id: 'prerequisites', label: 'Prerequisites' },
  { id: 'bucket', label: 'Create the S3 bucket' },
  { id: 'bucket-settings', label: 'Bucket settings reference' },
  { id: 'iam-user', label: 'IAM user & access model' },
  { id: 'iam-policy', label: 'The IAM policy JSON' },
  { id: 'arns', label: 'Understanding ARNs' },
  { id: 'iam-create', label: 'Create & attach the policy' },
  { id: 'access-key', label: 'Create the access key' },
  { id: 'sp-provider', label: 'Service Portal: add the provider' },
  { id: 'connection-test', label: 'Connection test' },
  { id: 'migration-overview', label: 'Storage migration explained' },
  { id: 'pre-migration', label: 'Pre-migration checklist' },
  { id: 'dry-run', label: 'Dry run & preflight' },
  { id: 'execute', label: 'Executing the migration' },
  { id: 'verification', label: 'Post-migration verification' },
  { id: 'security', label: 'Security best practices' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
];
const TOC_IDS = TOC.map((t) => t.id);

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export function AwsS3SetupGuidePage() {
  const active = useTocScrollSpy(TOC_IDS, TOC[0].id);

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-6">
      {/* ---- Header / contextual navigation ---- */}
      <header className="mb-6">
        <Link
          to={PLATFORM_ROUTES.STORAGE_PROVIDERS}
          className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back to Storage Providers
        </Link>
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FontAwesomeIcon icon={faBookOpen} className="h-5 w-5 text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              AWS S3 Storage Provider — Setup &amp; Migration Guide
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              A complete, step-by-step operational guide for configuring an Amazon S3 bucket and
              migrating Service Portal storage into it. Written for administrators who have never
              configured AWS S3 before.
            </p>
          </div>
        </div>

        <Callout tone="tip" title="Working across two tabs">
          <p>
            This guide opened in a new tab. Your <strong>Add / Configure Provider</strong> form is
            still open, untouched, in the original tab — switch back to it at any time and nothing
            you typed will be lost. Keep this guide on a second monitor while you work through the
            AWS Console.
          </p>
        </Callout>
      </header>

      <div className="gap-8 lg:grid lg:grid-cols-[240px_minmax(0,1fr)]">
        {/* ---- Sticky table of contents ---- */}
        <GuideToc items={TOC} active={active} />

        {/* ---- Content ---- */}
        <main className="min-w-0 space-y-8">
          {/* 1 ------------------------------------------------------ */}
          <section id="overview" className="scroll-mt-24">
            <h2 className="mb-4 text-xl font-bold text-foreground">
              <span className="mr-2 text-primary">1.</span>Overview &amp; architecture
            </h2>
            <div className="space-y-3 text-sm leading-relaxed text-foreground">
              <p>
                Service Portal stores every uploaded file through a <strong>storage adapter</strong>
                . Business code never touches a filesystem or an S3 API directly — it calls the
                adapter, and the adapter routes to whichever driver the active provider uses. Adding
                Amazon S3 means configuring the <Mono>s3</Mono> driver with a bucket and
                credentials, and then (optionally) migrating existing assets into it.
              </p>
              <p>
                The whole process is two independent halves. <strong>Configuring a provider</strong>{' '}
                is safe and reversible: nothing moves, and no existing file is touched.{' '}
                <strong>Migrating</strong> is the governed part, with a dry run, an approval, twenty
                finalization gates, and a deliberate rule that the source copy is never deleted.
              </p>

              <Figure
                n={1}
                caption="How Service Portal talks to Amazon S3"
                look="The application only ever issues six S3 commands. Those six commands are the entire basis for the IAM policy in section 6 — nothing more is requested."
              >
                <svg
                  viewBox="0 0 860 250"
                  className="h-auto w-full min-w-[640px]"
                  role="img"
                  aria-label="Architecture diagram: Service Portal business code calls the storage adapter, which resolves the S3 driver, which issues six AWS SDK commands to the bucket."
                >
                  <rect
                    x="10"
                    y="90"
                    width="150"
                    height="60"
                    rx="8"
                    className="fill-background stroke-border"
                    strokeWidth="1.5"
                  />
                  <text x="85" y="115" textAnchor="middle" className="fill-foreground text-[12px]">
                    Business code
                  </text>
                  <text
                    x="85"
                    y="133"
                    textAnchor="middle"
                    className="fill-muted-foreground text-[10px]"
                  >
                    uploads, downloads
                  </text>

                  <rect
                    x="200"
                    y="90"
                    width="150"
                    height="60"
                    rx="8"
                    className="fill-background stroke-border"
                    strokeWidth="1.5"
                  />
                  <text x="275" y="115" textAnchor="middle" className="fill-foreground text-[12px]">
                    StorageAdapter
                  </text>
                  <text
                    x="275"
                    y="133"
                    textAnchor="middle"
                    className="fill-muted-foreground text-[10px]"
                  >
                    driver registry
                  </text>

                  <rect
                    x="390"
                    y="90"
                    width="150"
                    height="60"
                    rx="8"
                    className="fill-primary/10 stroke-primary"
                    strokeWidth="1.5"
                  />
                  <text x="465" y="115" textAnchor="middle" className="fill-foreground text-[12px]">
                    s3 driver
                  </text>
                  <text
                    x="465"
                    y="133"
                    textAnchor="middle"
                    className="fill-muted-foreground text-[10px]"
                  >
                    AWS SDK v3
                  </text>

                  <rect
                    x="620"
                    y="40"
                    width="220"
                    height="170"
                    rx="8"
                    className="fill-background stroke-border"
                    strokeWidth="1.5"
                  />
                  <text x="730" y="64" textAnchor="middle" className="fill-foreground text-[12px]">
                    Amazon S3 bucket
                  </text>
                  {[
                    'PutObject',
                    'GetObject',
                    'DeleteObject',
                    'CopyObject',
                    'HeadObject',
                    'HeadBucket / ListObjectsV2',
                  ].map((cmd, i) => (
                    <text
                      key={cmd}
                      x="640"
                      y={90 + i * 20}
                      className="fill-muted-foreground font-mono text-[10px]"
                    >
                      • {cmd}
                    </text>
                  ))}

                  {[
                    [160, 200],
                    [350, 390],
                    [540, 620],
                  ].map(([x1, x2]) => (
                    <line
                      key={x1}
                      x1={x1}
                      y1="120"
                      x2={x2}
                      y2="120"
                      className="stroke-muted-foreground"
                      strokeWidth="1.5"
                      markerEnd="url(#arrow)"
                    />
                  ))}
                  <defs>
                    <marker
                      id="arrow"
                      markerWidth="8"
                      markerHeight="8"
                      refX="7"
                      refY="4"
                      orient="auto"
                    >
                      <path d="M0,0 L8,4 L0,8 z" className="fill-muted-foreground" />
                    </marker>
                  </defs>
                </svg>
              </Figure>

              <Callout tone="note" title="What this guide will not tell you to do">
                <p>
                  It will not tell you to make the bucket public, to grant <Mono>s3:*</Mono>, or to
                  delete source files after a migration. Each of those is either unnecessary for
                  Service Portal or actively unsafe, and sections 17 and 12 explain why.
                </p>
              </Callout>
            </div>
          </section>

          {/* 2 ------------------------------------------------------ */}
          <Section id="prerequisites" n={2} title="Prerequisites">
            <Table
              head={['You need', 'Why', 'Where']}
              rows={[
                [
                  'An AWS account with permission to create S3 buckets and IAM policies',
                  'You will create one bucket, one policy, one IAM user and one access key',
                  <span key="a">
                    <Mono>console.aws.amazon.com</Mono>
                  </span>,
                ],
                [
                  <span key="b">
                    Service Portal <Mono>platform:system:manage</Mono> permission
                  </span>,
                  'Required to configure a provider and to run a migration. Viewing is platform:system:view',
                  'Platform Console → your role',
                ],
                [
                  'A maintenance window (migration only)',
                  'Configuring a provider needs none. A migration copies every object and should be watched',
                  '—',
                ],
                [
                  'A current backup (migration only)',
                  'Standard practice before a bulk operation, even though migration never deletes the source',
                  'Your database + volume backup procedure',
                ],
              ]}
            />
            <Callout tone="note">
              <p>
                Adding an S3 provider does <strong>not</strong> require downtime and does not change
                where existing files live. Existing assets stay exactly where they are until you
                deliberately run a migration.
              </p>
            </Callout>
          </Section>

          {/* 3 ------------------------------------------------------ */}
          <Section id="bucket" n={3} title="Create the S3 bucket">
            <ol className="ml-5 list-decimal space-y-2">
              <li>
                Sign in at <Mono>https://console.aws.amazon.com/</Mono>. Use an IAM user with
                administrative rights, or the account root only if nothing else exists yet.
              </li>
              <li>
                In the top search bar type <strong>S3</strong> and open the <strong>S3</strong>{' '}
                service.
              </li>
              <li>
                Choose <strong>Buckets</strong> in the left navigation, then{' '}
                <strong>Create bucket</strong>.
              </li>
              <li>
                <strong>AWS Region</strong> — pick the region closest to your users, or the one your
                data-residency policy requires. Note the region code exactly as shown, for example{' '}
                <Mono>ap-south-1</Mono>. You will type this into Service Portal, and it must match.
              </li>
              <li>
                <strong>Bucket name</strong> — globally unique across all of AWS. See naming rules
                below.
              </li>
              <li>
                Leave <strong>Object Ownership</strong> at{' '}
                <strong>ACLs disabled (recommended)</strong>.
              </li>
              <li>
                Leave <strong>Block all public access</strong> fully <strong>enabled</strong>.
              </li>
              <li>
                Set <strong>Bucket Versioning</strong> per the guidance below (we recommend{' '}
                <strong>Enable</strong>).
              </li>
              <li>
                Leave <strong>Default encryption</strong> on (<Mono>SSE-S3</Mono> is the default).
              </li>
              <li>
                Select <strong>Create bucket</strong> and confirm it appears in the bucket list with
                the region you chose.
              </li>
            </ol>

            <h3 className="mt-5 text-base font-semibold text-foreground">Bucket naming rules</h3>
            <Table
              head={['Rule', 'Detail']}
              rows={[
                ['Length', '3–63 characters'],
                ['Characters', 'Lowercase letters, numbers, hyphens. No uppercase, no underscores'],
                ['Start / end', 'Must start and end with a letter or number'],
                ['Uniqueness', 'Globally unique across every AWS account in the partition'],
                ['Not IP-like', 'Must not be formatted as an IP address'],
                [
                  'Recommended shape',
                  <span key="s">
                    <Mono>acme-serviceportal-prod-storage</Mono> — organisation, product,
                    environment, purpose
                  </span>,
                ],
              ]}
            />
            <Callout tone="tip">
              <p>
                Put the environment in the name. A bucket called{' '}
                <Mono>acme-serviceportal-staging-storage</Mono> can never be confused with
                production during a late-night change.
              </p>
            </Callout>

            <Figure
              n={2}
              caption="AWS Console → S3 → Create bucket"
              look="The Region selector at the top and the bucket name field. Confirm the region code before you continue — it cannot be changed after the bucket is created."
            >
              <svg
                viewBox="0 0 760 260"
                className="h-auto w-full min-w-[600px]"
                role="img"
                aria-label="Wireframe of the AWS S3 Create bucket screen showing region selector, bucket name field, object ownership and block public access sections."
              >
                <rect
                  x="10"
                  y="10"
                  width="740"
                  height="240"
                  rx="6"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="30" y="38" className="fill-foreground text-[13px] font-semibold">
                  Create bucket
                </text>
                <line x1="10" y1="50" x2="750" y2="50" className="stroke-border" strokeWidth="1" />

                <text x="30" y="76" className="fill-muted-foreground text-[10px]">
                  AWS Region
                </text>
                <rect
                  x="30"
                  y="84"
                  width="300"
                  height="26"
                  rx="4"
                  className="fill-primary/10 stroke-primary"
                  strokeWidth="1.5"
                />
                <text x="40" y="101" className="fill-foreground font-mono text-[11px]">
                  ap-south-1 (Asia Pacific — Mumbai)
                </text>

                <text x="30" y="136" className="fill-muted-foreground text-[10px]">
                  Bucket name
                </text>
                <rect
                  x="30"
                  y="144"
                  width="420"
                  height="26"
                  rx="4"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1.5"
                />
                <text x="40" y="161" className="fill-foreground font-mono text-[11px]">
                  acme-serviceportal-prod-storage
                </text>

                <text x="30" y="196" className="fill-muted-foreground text-[10px]">
                  Object Ownership
                </text>
                <text x="30" y="212" className="fill-foreground text-[11px]">
                  ◉ ACLs disabled (recommended)
                </text>

                <text x="430" y="196" className="fill-muted-foreground text-[10px]">
                  Block Public Access
                </text>
                <text x="430" y="212" className="fill-foreground text-[11px]">
                  ☑ Block all public access
                </text>
              </svg>
            </Figure>
          </Section>

          {/* 4 ------------------------------------------------------ */}
          <Section id="bucket-settings" n={4} title="Bucket settings reference">
            <p>Select exactly these values. The rationale column explains each one.</p>
            <Table
              head={['Setting', 'Value', 'Why']}
              rows={[
                [
                  'AWS Region',
                  <Mono key="r">the region nearest your users</Mono>,
                  'Cannot be changed later. Must match the Region field in Service Portal exactly.',
                ],
                [
                  'Object Ownership',
                  <strong key="o">ACLs disabled (recommended)</strong>,
                  'Service Portal never sets an object ACL — the driver issues no ACL parameter at all. Access is controlled entirely by the IAM policy, so ACLs are dead weight and a common source of confusion.',
                ],
                [
                  'Block all public access',
                  <strong key="p">ON — all four sub-options</strong>,
                  'Service Portal serves files through time-limited signed URLs generated by the application. It never needs public objects. A public bucket would expose customer files.',
                ],
                [
                  'Bucket Versioning',
                  <strong key="v">Enable (recommended)</strong>,
                  'Versioning keeps a recoverable copy when an object is overwritten or deleted. The application is version-unaware and never sends a VersionId, so enabling it changes nothing functionally — but it is the difference between a recoverable incident and permanent loss.',
                ],
                [
                  'Default encryption',
                  <span key="e">
                    <strong>SSE-S3</strong> (<Mono>AES256</Mono>) — the AWS default
                  </span>,
                  'Encrypts objects at rest transparently. The driver sends no encryption parameters, so bucket-level default encryption is what applies.',
                ],
                [
                  'SSE-KMS',
                  'Optional',
                  'Works, but the IAM principal then also needs kms:Decrypt and kms:GenerateDataKey on the key, which is NOT in the policy in section 6. Only choose KMS if you will add those permissions deliberately.',
                ],
                [
                  'Bucket Key',
                  'Enabled if using KMS',
                  'Reduces KMS request costs. No effect on Service Portal.',
                ],
                [
                  'Object Lock',
                  <strong key="l">Disable</strong>,
                  'Object Lock prevents deletion and overwriting. Service Portal deletes objects during normal lifecycle operations, so Object Lock will cause failures.',
                ],
                [
                  'Lifecycle rules',
                  <strong key="lc">None initially</strong>,
                  'Do not add an expiration rule. Service Portal has its own asset lifecycle with quarantine and retention. An S3-side expiry can delete objects the application still considers live.',
                ],
              ]}
            />

            <Callout tone="warning" title="Object Lock and S3 lifecycle expiry">
              <p>
                Both of these operate <em>below</em> Service Portal and it cannot see them. Object
                Lock will break deletes; an S3 expiry rule will silently remove files the asset
                catalogue still lists, which surfaces later as missing objects during a migration
                gate check.
              </p>
            </Callout>

            <h3 className="mt-5 text-base font-semibold text-foreground">Verifying the bucket</h3>
            <ol className="ml-5 list-decimal space-y-1.5">
              <li>
                S3 → <strong>Buckets</strong>: your bucket is listed, with the correct{' '}
                <strong>AWS Region</strong> in that column. Record the region code.
              </li>
              <li>
                Open the bucket → <strong>Properties</strong>: confirm{' '}
                <strong>Bucket Versioning</strong> and <strong>Default encryption</strong>.
              </li>
              <li>
                <strong>Permissions</strong> tab: <strong>Block all public access</strong> reads{' '}
                <em>On</em>.
              </li>
              <li>
                The <strong>bucket ARN</strong> is shown on the Properties tab as{' '}
                <Mono>arn:aws:s3:::YOUR-BUCKET-NAME</Mono>. You will need it in section 6.
              </li>
            </ol>
          </Section>

          {/* 5 ------------------------------------------------------ */}
          <Section id="iam-user" n={5} title="IAM user & access model">
            <Callout tone="note" title="Service Portal uses a long-lived access key pair">
              <p>
                The provider configuration has exactly two credential fields —{' '}
                <Mono>accessKeyId</Mono> and <Mono>secretAccessKey</Mono>. That is an{' '}
                <strong>IAM user access key</strong>. There is no field for a role ARN, an external
                ID, a session token or a web-identity token, and the driver does not attempt
                instance-profile or role-assumption credential resolution for a configured provider.
                So this guide documents the IAM-user flow, because that is what the product
                implements.
              </p>
            </Callout>

            <Table
              head={['Identity type', 'Supported here?', 'Notes']}
              rows={[
                [
                  <strong key="u">IAM user + access key</strong>,
                  <span key="y" className="font-semibold text-primary">
                    Yes — this is the model
                  </span>,
                  'Create a dedicated user for Service Portal. Do not reuse a human administrator’s key.',
                ],
                [
                  'IAM role assumed via STS',
                  'Not configurable',
                  'No role-ARN or session-token field exists in the provider schema.',
                ],
                [
                  'EC2 / ECS instance profile',
                  'Not used for a configured provider',
                  'The driver builds its client from the stored credentials.',
                ],
              ]}
            />

            <h3 className="mt-5 text-base font-semibold text-foreground">Create the IAM user</h3>
            <ol className="ml-5 list-decimal space-y-1.5">
              <li>
                Console search → <strong>IAM</strong> → <strong>Users</strong> →{' '}
                <strong>Create user</strong>.
              </li>
              <li>
                Name it for its job, e.g. <Mono>service-portal-prod</Mono> or{' '}
                <Mono>service-portal-staging</Mono>.
              </li>
              <li>
                Do <strong>not</strong> tick “Provide user access to the AWS Management Console”.
                This identity is for the application only and never needs to sign in.
              </li>
              <li>
                Skip permissions for now — you will attach the policy in section 8 — then{' '}
                <strong>Create user</strong>.
              </li>
            </ol>

            <Callout tone="security" title="Credential handling — non-negotiable">
              <ul className="ml-4 list-disc space-y-1">
                <li>
                  One dedicated IAM user per environment. Never share a key across environments.
                </li>
                <li>
                  Never put AWS keys in source code, in frontend code, or in a committed{' '}
                  <Mono>.env</Mono>.
                </li>
                <li>
                  Never paste a Secret Access Key into a support ticket, chat, screenshot, log or
                  bug report. If it is ever exposed, treat it as compromised and rotate immediately.
                </li>
                <li>
                  Store it in your organisation’s secret manager. Once entered into Service Portal
                  it is encrypted at rest and never displayed again.
                </li>
              </ul>
            </Callout>
          </Section>

          {/* 6 ------------------------------------------------------ */}
          <Section id="iam-policy" n={6} title="The IAM policy JSON">
            <p>
              These permissions are derived from the six AWS SDK commands the driver actually
              issues. Nothing here is speculative and nothing extra is requested.
            </p>

            <h3 className="mt-4 text-base font-semibold text-foreground">
              A. Normal production policy
            </h3>
            <p>
              Replace <Mono>YOUR-BUCKET-NAME</Mono> in <strong>both</strong> places. Section 7
              explains why one has <Mono>/*</Mono> and the other does not.
            </p>
            <CodeBlock label="ServicePortalS3Access — production policy" code={PRODUCTION_POLICY} />

            <Table
              head={['Action', 'Used by', 'Scope', 'Required for']}
              rows={[
                [
                  <Mono key="1">s3:ListBucket</Mono>,
                  'HeadBucket (connection test), ListObjectsV2 (provider usage statistics, object listing)',
                  <strong key="s1">Bucket ARN</strong>,
                  'Normal operation. Without it the connection test fails immediately.',
                ],
                [
                  <Mono key="2">s3:GetObject</Mono>,
                  'GetObject (download, checksum), HeadObject (exists), CopyObject (read side), signed URL generation',
                  <strong key="s2">Object ARN /*</strong>,
                  'Normal operation, and every migration verification step.',
                ],
                [
                  <Mono key="3">s3:PutObject</Mono>,
                  'PutObject (upload), CopyObject (write side)',
                  <strong key="s3">Object ARN /*</strong>,
                  'Normal operation, and the copy phase of a migration.',
                ],
                [
                  <Mono key="4">s3:DeleteObject</Mono>,
                  'DeleteObject',
                  <strong key="s4">Object ARN /*</strong>,
                  'Normal operation — asset deletion and lifecycle. NOT used to remove migration sources.',
                ],
              ]}
            />

            <Callout tone="tip" title="Four actions is the whole list">
              <p>
                A migration needs <strong>no additional S3 permissions</strong>. The migration
                runner, the verifier and the finalizer all go through the same driver methods as
                normal operation — copy uses <Mono>GetObject</Mono> plus <Mono>PutObject</Mono>, and
                verification re-downloads objects with <Mono>GetObject</Mono>. If you have this
                policy, you can migrate.
              </p>
            </Callout>

            <h3 className="mt-5 text-base font-semibold text-foreground">
              B. Temporary / recovery-only permissions
            </h3>
            <p>
              The application never sends a <Mono>VersionId</Mono> and never calls{' '}
              <Mono>ListObjectVersions</Mono> — it is entirely version-unaware. So version
              permissions are <strong>not</strong> part of normal operation and must not be added to
              the policy above.
            </p>
            <Table
              head={['Action', 'When it is needed', 'Who uses it']}
              rows={[
                [
                  <Mono key="v1">s3:ListBucketVersions</Mono>,
                  'Only during a data-loss investigation, to enumerate noncurrent versions and delete markers',
                  'A standalone operator script — never the application',
                ],
                [
                  <Mono key="v2">s3:GetObjectVersion</Mono>,
                  'Only to read a specific historical version during recovery',
                  'A standalone operator script — never the application',
                ],
                [
                  <Mono key="v3">s3:GetBucketVersioning</Mono>,
                  'Only to confirm versioning status during an investigation',
                  'A standalone operator script',
                ],
              ]}
            />
            <Callout tone="security" title="Keep recovery permissions separate and temporary">
              <p>
                If you ever need these, put them in a <strong>separate, second policy</strong>,
                attach it for the duration of the investigation, scope it with an{' '}
                <Mono>s3:prefix</Mono> condition and to specific object ARNs, and{' '}
                <strong>detach it when the work is done</strong>. Never fold them into the
                production policy — the running application has no use for them, and least privilege
                is the entire point.
              </p>
            </Callout>

            <Callout tone="warning" title="If you enabled SSE-KMS">
              <p>
                The policy above is complete for <Mono>SSE-S3</Mono>. With a customer-managed KMS
                key you must additionally grant <Mono>kms:Decrypt</Mono> and{' '}
                <Mono>kms:GenerateDataKey</Mono> on that key ARN, or every upload and download will
                fail with an access error that mentions KMS rather than S3.
              </p>
            </Callout>
          </Section>

          {/* 7 ------------------------------------------------------ */}
          <Section id="arns" n={7} title="Understanding ARNs">
            <p>
              An ARN is an Amazon Resource Name — a unique identifier for an AWS resource. S3
              permissions are split across two different ARN shapes, and getting this wrong is the
              single most common cause of a policy that looks right and does not work.
            </p>
            <Table
              head={['ARN', 'Refers to', 'Actions that belong here']}
              rows={[
                [
                  <Mono key="b">arn:aws:s3:::YOUR-BUCKET-NAME</Mono>,
                  'The bucket container itself — its existence and its listing',
                  <Mono key="ab">s3:ListBucket</Mono>,
                ],
                [
                  <Mono key="o">arn:aws:s3:::YOUR-BUCKET-NAME/*</Mono>,
                  'Every object inside the bucket',
                  <span key="ao">
                    <Mono>s3:GetObject</Mono>, <Mono>s3:PutObject</Mono>,{' '}
                    <Mono>s3:DeleteObject</Mono>
                  </span>,
                ],
              ]}
            />
            <Callout tone="warning" title="The classic mistake">
              <p>
                Putting <Mono>s3:ListBucket</Mono> on the <Mono>/*</Mono> ARN. It will never work —
                listing is a bucket-level operation, so the permission must be on the bucket ARN
                with no trailing <Mono>/*</Mono>. The symptom is a connection test that fails with{' '}
                <Mono>AccessDenied</Mono> even though the object permissions are correct.
              </p>
            </Callout>

            <h3 className="mt-4 text-base font-semibold text-foreground">
              Placeholders to replace
            </h3>
            <Table
              head={['Placeholder', 'Example', 'Where to find it']}
              rows={[
                [
                  <Mono key="1">YOUR-BUCKET-NAME</Mono>,
                  <Mono key="2">acme-serviceportal-prod-storage</Mono>,
                  'S3 → Buckets → the Name column',
                ],
                [
                  'Bucket ARN',
                  <Mono key="3">arn:aws:s3:::acme-serviceportal-prod-storage</Mono>,
                  'S3 → your bucket → Properties',
                ],
                [
                  'Object ARN',
                  <Mono key="4">arn:aws:s3:::acme-serviceportal-prod-storage/*</Mono>,
                  'Bucket ARN with /* appended',
                ],
                [
                  'Account ID',
                  <Mono key="5">123456789012</Mono>,
                  'Top-right account menu. Not needed in this policy — S3 ARNs omit account and region.',
                ],
              ]}
            />

            <h3 className="mt-4 text-base font-semibold text-foreground">
              A note on prefix conditions
            </h3>
            <p>
              You may be tempted to restrict <Mono>s3:ListBucket</Mono> with an{' '}
              <Mono>s3:prefix</Mono> condition. <strong>Do not do this</strong> for the Service
              Portal bucket: the provider usage statistics feature performs a full paginated{' '}
              <Mono>ListObjectsV2</Mono> over the whole bucket, and a prefix condition will make it
              fail. Use a dedicated bucket for Service Portal instead of sharing one and trying to
              fence it off with prefixes.
            </p>
          </Section>

          {/* 8 ------------------------------------------------------ */}
          <Section id="iam-create" n={8} title="Create & attach the policy">
            <ol className="ml-5 list-decimal space-y-1.5">
              <li>
                Console search → <strong>IAM</strong>.
              </li>
              <li>
                Left navigation → <strong>Policies</strong>.
              </li>
              <li>
                <strong>Create policy</strong>.
              </li>
              <li>
                Switch the editor from <strong>Visual</strong> to <strong>JSON</strong>.
              </li>
              <li>Delete the placeholder content and paste the policy from section 6A.</li>
              <li>
                Replace <Mono>YOUR-BUCKET-NAME</Mono> in <strong>both</strong> statements.
              </li>
              <li>
                <strong>Next</strong>.
              </li>
              <li>
                Policy name: <Mono>ServicePortalS3Access</Mono> (add an environment suffix if you
                run more than one, e.g. <Mono>ServicePortalS3Access-Staging</Mono>).
              </li>
              <li>
                Description:{' '}
                <em>Least-privilege S3 access for the Service Portal storage adapter.</em>
              </li>
              <li>
                Review that exactly two statements are listed — one bucket-level, one object-level —
                then <strong>Create policy</strong>.
              </li>
              <li>
                Go to <strong>Users</strong> → <Mono>service-portal-prod</Mono> →{' '}
                <strong>Add permissions</strong> → <strong>Attach policies directly</strong> →
                select <Mono>ServicePortalS3Access</Mono> → <strong>Add permissions</strong>.
              </li>
            </ol>

            <Figure
              n={3}
              caption="IAM → Policies → Create policy → JSON editor"
              look="The JSON tab must be selected. After pasting, confirm no YOUR-BUCKET-NAME placeholder remains — the console will not warn you about an unreplaced placeholder."
            >
              <svg
                viewBox="0 0 760 240"
                className="h-auto w-full min-w-[600px]"
                role="img"
                aria-label="Wireframe of the IAM policy JSON editor with the JSON tab selected and policy text pasted."
              >
                <rect
                  x="10"
                  y="10"
                  width="740"
                  height="220"
                  rx="6"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="30" y="36" className="fill-foreground text-[13px] font-semibold">
                  Specify permissions
                </text>
                <rect
                  x="30"
                  y="48"
                  width="70"
                  height="24"
                  rx="4"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1"
                />
                <text x="50" y="64" className="fill-muted-foreground text-[11px]">
                  Visual
                </text>
                <rect
                  x="104"
                  y="48"
                  width="70"
                  height="24"
                  rx="4"
                  className="fill-primary/15 stroke-primary"
                  strokeWidth="1.5"
                />
                <text x="128" y="64" className="fill-foreground text-[11px] font-semibold">
                  JSON
                </text>
                <rect
                  x="30"
                  y="84"
                  width="700"
                  height="132"
                  rx="4"
                  className="fill-muted/30 stroke-border"
                  strokeWidth="1"
                />
                {[
                  '{',
                  '  "Version": "2012-10-17",',
                  '  "Statement": [',
                  '    { "Effect": "Allow", "Action": "s3:ListBucket",',
                  '      "Resource": "arn:aws:s3:::acme-serviceportal-prod-storage" },',
                  '    { "Effect": "Allow", "Action": ["s3:GetObject","s3:PutObject","s3:DeleteObject"],',
                  '      "Resource": "arn:aws:s3:::acme-serviceportal-prod-storage/*" } ] }',
                ].map((ln, i) => (
                  <text
                    key={i}
                    x="42"
                    y={104 + i * 16}
                    className="fill-foreground font-mono text-[9px]"
                  >
                    {ln}
                  </text>
                ))}
              </svg>
            </Figure>

            <h3 className="mt-4 text-base font-semibold text-foreground">
              Verify the policy is attached
            </h3>
            <p>
              IAM → <strong>Users</strong> → <Mono>service-portal-prod</Mono> →{' '}
              <strong>Permissions</strong> tab. <Mono>ServicePortalS3Access</Mono> must be listed.
              If it is not, the connection test in section 11 will fail with{' '}
              <Mono>AccessDenied</Mono>.
            </p>
          </Section>

          {/* 9 ------------------------------------------------------ */}
          <Section id="access-key" n={9} title="Create the access key">
            <ol className="ml-5 list-decimal space-y-1.5">
              <li>
                IAM → <strong>Users</strong> → <Mono>service-portal-prod</Mono> →{' '}
                <strong>Security credentials</strong> tab.
              </li>
              <li>
                Scroll to <strong>Access keys</strong> → <strong>Create access key</strong>.
              </li>
              <li>
                Use case: choose <strong>Application running outside AWS</strong> (or{' '}
                <strong>Third-party service</strong> — the wording varies). Both produce a standard
                long-lived key pair, which is what Service Portal needs.
              </li>
              <li>Optional description tag, e.g. “Service Portal production storage”.</li>
              <li>
                <strong>Create access key</strong>.
              </li>
              <li>
                Copy the <strong>Access key ID</strong> (begins <Mono>AKIA…</Mono>) and the{' '}
                <strong>Secret access key</strong>.
              </li>
            </ol>

            <Callout tone="warning" title="This is the only time the secret is ever shown">
              <p>
                AWS displays the Secret Access Key exactly once, on this screen. Leave the page and
                it can never be retrieved — not by you, not by AWS Support. Copy it into your secret
                manager, or straight into the Service Portal form, before navigating away.
              </p>
              <p>
                If you lose it: create a new access key, update Service Portal, verify with a
                connection test, and only then delete the old key.
              </p>
            </Callout>

            <Figure
              n={4}
              caption="IAM → Security credentials → Create access key (retrieve step)"
              look="The Secret access key is hidden behind a Show toggle and is visible only on this screen. Once you click Done it is gone permanently."
            >
              <svg
                viewBox="0 0 760 190"
                className="h-auto w-full min-w-[600px]"
                role="img"
                aria-label="Wireframe of the AWS access key retrieval screen showing the access key ID and a masked secret access key with a Show toggle."
              >
                <rect
                  x="10"
                  y="10"
                  width="740"
                  height="170"
                  rx="6"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="30" y="36" className="fill-foreground text-[13px] font-semibold">
                  Retrieve access keys
                </text>
                <text x="30" y="66" className="fill-muted-foreground text-[10px]">
                  Access key
                </text>
                <rect
                  x="30"
                  y="74"
                  width="320"
                  height="24"
                  rx="4"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1"
                />
                <text x="40" y="90" className="fill-foreground font-mono text-[10px]">
                  AKIAIOSFODNN7EXAMPLE
                </text>
                <text x="30" y="122" className="fill-muted-foreground text-[10px]">
                  Secret access key
                </text>
                <rect
                  x="30"
                  y="130"
                  width="320"
                  height="24"
                  rx="4"
                  className="fill-muted/40 stroke-border"
                  strokeWidth="1"
                />
                <text x="40" y="146" className="fill-foreground font-mono text-[10px]">
                  ●●●●●●●●●●●●●●●●●●●●
                </text>
                <rect
                  x="360"
                  y="130"
                  width="56"
                  height="24"
                  rx="4"
                  className="fill-background stroke-border"
                  strokeWidth="1"
                />
                <text x="374" y="146" className="fill-foreground text-[10px]">
                  Show
                </text>
                <rect
                  x="440"
                  y="70"
                  width="290"
                  height="84"
                  rx="4"
                  className="fill-destructive/10 stroke-destructive/40"
                  strokeWidth="1"
                />
                <text x="452" y="94" className="fill-foreground text-[10px] font-semibold">
                  Shown once. Not retrievable later.
                </text>
                <text x="452" y="112" className="fill-muted-foreground text-[10px]">
                  Copy both values now, then store the
                </text>
                <text x="452" y="128" className="fill-muted-foreground text-[10px]">
                  secret in your secret manager.
                </text>
              </svg>
            </Figure>
          </Section>

          {/* 10 ----------------------------------------------------- */}
          <Section id="sp-provider" n={10} title="Service Portal: add the provider">
            <p>
              Switch back to your first tab — the provider list is still open. Find the{' '}
              <strong>Amazon S3</strong> card and select <strong>Configure</strong> (or{' '}
              <strong>Edit</strong> if it already has configuration). A dialog opens with exactly
              five fields.
            </p>

            <Table
              head={[
                'Field',
                'Required',
                'What to enter',
                'Example',
                'Where to get it',
                'Security',
              ]}
              rows={[
                [
                  <strong key="1">Region</strong>,
                  <span key="r1" className="font-semibold text-primary">
                    Yes
                  </span>,
                  'The AWS region code of your bucket. Lowercase, hyphenated. Not the display name.',
                  <Mono key="e1">ap-south-1</Mono>,
                  'S3 → Buckets → AWS Region column',
                  'Not secret',
                ],
                [
                  <strong key="2">Bucket Name</strong>,
                  <span key="r2" className="font-semibold text-primary">
                    Yes
                  </span>,
                  'The bucket name only — no s3:// scheme, no ARN, no trailing slash, no path.',
                  <Mono key="e2">acme-serviceportal-prod-storage</Mono>,
                  'S3 → Buckets → Name column',
                  'Not secret',
                ],
                [
                  <strong key="3">Access Key ID</strong>,
                  <span key="r3" className="font-semibold text-primary">
                    Yes
                  </span>,
                  'The access key ID of the dedicated IAM user.',
                  <Mono key="e3">AKIAIOSFODNN7EXAMPLE</Mono>,
                  'IAM → user → Security credentials',
                  <strong key="s3f">Encrypted at rest</strong>,
                ],
                [
                  <strong key="4">Secret Access Key</strong>,
                  <span key="r4" className="font-semibold text-primary">
                    Yes
                  </span>,
                  'The secret shown once at key creation. See the blank-field rule below.',
                  <em key="e4">(never displayed)</em>,
                  'Your secret manager',
                  <strong key="s4f">Encrypted at rest, never shown again</strong>,
                ],
                [
                  <strong key="5">Custom Endpoint</strong>,
                  <span key="r5" className="text-muted-foreground">
                    No
                  </span>,
                  'Leave EMPTY for Amazon S3. Only for S3-compatible services that are not AWS.',
                  <em key="e5">(leave blank)</em>,
                  '—',
                  'Not secret',
                ],
              ]}
            />

            <Callout tone="warning" title="Leave Custom Endpoint empty for AWS">
              <p>
                Supplying an endpoint switches the client to <strong>path-style addressing</strong>,
                which is derived automatically from the presence of an endpoint. For real Amazon S3
                you want the default virtual-hosted style, so the field must stay blank. Filling it
                in “just to be explicit” will change client behaviour.
              </p>
            </Callout>

            <Callout tone="security" title="The blank-secret rule">
              <p>
                When you reopen the dialog on an already-configured provider, the secret fields
                start <strong>empty</strong> and the hint reads{' '}
                <em>“Leave blank to keep the current value.”</em> That is deliberate. Stored secrets
                are masked when read back, and submitting the mask would overwrite your real
                credential with the mask characters. So: leave a secret field blank unless you are
                genuinely replacing it.
              </p>
            </Callout>

            <Callout tone="note">
              <p>
                There is no prefix, folder, path, ACL or storage-class field, because the driver has
                no such setting. Service Portal generates its own object keys —{' '}
                <Mono>tenant/&lt;tenantId&gt;/&lt;module&gt;/…</Mono> — and writes them at the root
                of the bucket.
              </p>
            </Callout>

            <Figure
              n={5}
              caption="Service Portal → Storage Providers → Amazon S3 → Configure"
              look="Exactly five fields. Required fields are marked with a red asterisk. The two credential fields render as password inputs and are encrypted on save."
            >
              <svg
                viewBox="0 0 620 330"
                className="h-auto w-full min-w-[520px]"
                role="img"
                aria-label="Wireframe of the Service Portal Configure Amazon S3 dialog showing Region, Bucket Name, Access Key ID, Secret Access Key and Custom Endpoint fields."
              >
                <rect
                  x="10"
                  y="10"
                  width="600"
                  height="310"
                  rx="10"
                  className="fill-background stroke-border"
                  strokeWidth="1.5"
                />
                <text x="30" y="40" className="fill-foreground text-[13px] font-semibold">
                  Configure Amazon S3
                </text>
                {[
                  ['Region *', 'ap-south-1'],
                  ['Bucket Name *', 'acme-serviceportal-prod-storage'],
                  ['Access Key ID *', 'AKIAIOSFODNN7EXAMPLE'],
                  ['Secret Access Key *', '••••••••'],
                  ['Custom Endpoint', '(leave empty for AWS)'],
                ].map(([label, val], i) => (
                  <g key={label}>
                    <text
                      x="30"
                      y={70 + i * 48}
                      className="fill-muted-foreground text-[10px] font-medium"
                    >
                      {label}
                    </text>
                    <rect
                      x="30"
                      y={78 + i * 48}
                      width="550"
                      height="26"
                      rx="4"
                      className={cn('stroke-border', i === 4 ? 'fill-muted/20' : 'fill-muted/40')}
                      strokeWidth="1"
                    />
                    <text
                      x="40"
                      y={95 + i * 48}
                      className={cn(
                        'font-mono text-[10px]',
                        i === 4 ? 'fill-muted-foreground' : 'fill-foreground',
                      )}
                    >
                      {val}
                    </text>
                  </g>
                ))}
              </svg>
            </Figure>

            <p>
              Select <strong>Save</strong>. The provider is stored and the credential fields are
              encrypted. Saving does not move any file and does not make S3 the active provider.
            </p>
          </Section>

          {/* 11 ----------------------------------------------------- */}
          <Section id="connection-test" n={11} title="Connection test">
            <p>
              On the Amazon S3 card select <strong>Run Connection Test</strong>.
            </p>

            <Callout tone="warning" title="What the test actually checks — and what it does not">
              <p>
                The test issues a single <Mono>HeadBucket</Mono> call. It proves the bucket exists,
                the region is reachable, the credentials are valid, and the principal has{' '}
                <Mono>s3:ListBucket</Mono>.
              </p>
              <p>
                It does <strong>not</strong> upload, download or delete anything, so it does{' '}
                <strong>not</strong> prove that <Mono>s3:PutObject</Mono>, <Mono>s3:GetObject</Mono>{' '}
                or <Mono>s3:DeleteObject</Mono> are granted. A green result with a broken object
                policy is entirely possible — the failure will surface later on first upload or
                during a migration. Get the policy right; do not treat the test as full coverage.
              </p>
            </Callout>

            <p>On success the provider reports healthy and returns these non-secret coordinates:</p>
            <Table
              head={['Field', 'Meaning']}
              rows={[
                [
                  <Mono key="1">driverId</Mono>,
                  <span key="v1">
                    Always <Mono>s3</Mono>
                  </span>,
                ],
                [<Mono key="2">region</Mono>, 'The region the client resolved'],
                [<Mono key="3">bucket</Mono>, 'The bucket that answered'],
                [
                  <Mono key="4">endpoint</Mono>,
                  <span key="v4">
                    <Mono>aws-default</Mono> when Custom Endpoint is blank — which is what you want
                    for AWS
                  </span>,
                ],
                [
                  <Mono key="5">forcePathStyle</Mono>,
                  <span key="v5">
                    <Mono>false</Mono> for AWS. Becomes <Mono>true</Mono> only if you set a custom
                    endpoint
                  </span>,
                ],
              ]}
            />
            <p>
              Credentials are never included in the result. Latency is recorded, and the provider’s
              health status and last-checked timestamp are updated.
            </p>

            <h3 className="mt-4 text-base font-semibold text-foreground">
              Failure messages, mapped to cause
            </h3>
            <Table
              head={['Message contains', 'Cause', 'Fix']}
              rows={[
                [
                  <Mono key="1">Provider is not fully configured. Missing required keys</Mono>,
                  'One of region, bucket, accessKeyId or secretAccessKey has no stored value. This is a Service Portal check, raised before any AWS call.',
                  'Reopen Configure and complete the named field.',
                ],
                [
                  <Mono key="2">AccessDenied</Mono>,
                  'Credentials are valid but the principal lacks s3:ListBucket on the bucket ARN — very often because ListBucket was written against the /* object ARN.',
                  'Re-read section 7. ListBucket belongs on the bucket ARN with no trailing /*. Confirm the policy is attached to the user.',
                ],
                [
                  <Mono key="3">NoSuchBucket</Mono>,
                  'The bucket name is wrong, or the bucket was deleted.',
                  'Compare the Bucket Name field against S3 → Buckets, character for character. No s3:// prefix, no ARN, no trailing slash.',
                ],
                [
                  <Mono key="4">PermanentRedirect</Mono>,
                  <span key="c4">
                    Wrong region — the bucket lives elsewhere. Also seen as{' '}
                    <Mono>AuthorizationHeaderMalformed</Mono> naming the expected region.
                  </span>,
                  'Set Region to the bucket’s actual region code. The error text usually names it.',
                ],
                [
                  <Mono key="5">InvalidAccessKeyId</Mono>,
                  'The access key ID does not exist — typo, or the key was deleted.',
                  'Re-copy the Access Key ID from IAM, or create a new key pair.',
                ],
                [
                  <Mono key="6">SignatureDoesNotMatch</Mono>,
                  'Secret Access Key is wrong — usually a truncated paste or trailing whitespace.',
                  'Re-enter the secret. If lost, create a new access key and update the field.',
                ],
                [
                  <Mono key="7">ECONNREFUSED / ENOTFOUND / getaddrinfo</Mono>,
                  'Custom Endpoint is set to something unreachable, or egress is blocked.',
                  'Clear Custom Endpoint for AWS. Otherwise check firewall and DNS from the backend host.',
                ],
                [
                  <Mono key="8">Provider error [driver=s3]</Mono>,
                  'Generic driver-level wrapper. The underlying AWS message follows it.',
                  'Read the text after the prefix and match it to a row above.',
                ],
              ]}
            />
            <Callout tone="warning" title="Never fix AccessDenied by making the bucket public">
              <p>
                Public access is never the correct remedy. Service Portal serves files through
                signed URLs; every legitimate access path works with Block Public Access fully on.
                An <Mono>AccessDenied</Mono> is an IAM policy problem — fix the policy.
              </p>
            </Callout>
          </Section>

          {/* 12 ----------------------------------------------------- */}
          <Section id="migration-overview" n={12} title="Storage migration explained">
            <p>
              A provider migration copies every object owned by a <strong>source</strong> provider
              to a <strong>target</strong> provider, verifies each copy, moves catalogue{' '}
              <strong>ownership</strong> to the target, and finally records the migration complete.
              It is deliberately not a single button.
            </p>

            <Callout tone="tip" title="The source is never deleted">
              <p>
                The engine <strong>never</strong> deletes a source object. After a successful
                migration the bytes still exist on the old provider. That is the safety property the
                whole design rests on — and it is why one of the finalization gates checks that the
                source objects are still present.
              </p>
            </Callout>

            <h3 className="mt-4 text-base font-semibold text-foreground">Migration statuses</h3>
            <Table
              head={['Status', 'Meaning', 'Safe to stop here?']}
              rows={[
                [<Mono key="1">PENDING</Mono>, 'Planned, nothing executed yet.', 'Yes'],
                [<Mono key="2">DRY_RUN</Mono>, 'Dry run in progress.', 'Yes'],
                [
                  <Mono key="3">DRY_RUN_COMPLETE</Mono>,
                  'Dry run finished. This is the state from which you approve.',
                  'Yes',
                ],
                [
                  <Mono key="4">READY</Mono>,
                  'Approved, not yet running. Copying may be started.',
                  'Yes',
                ],
                [
                  <Mono key="5">RUNNING</Mono>,
                  'Objects are being copied to the target.',
                  'Pause rather than abandon',
                ],
                [<Mono key="6">PAUSED</Mono>, 'Copying suspended; may be resumed.', 'Yes'],
                [
                  <Mono key="7">VERIFYING</Mono>,
                  'All copying is done. Awaiting ownership flip and finalization. Nothing further happens on its own.',
                  'Yes — this is a stable resting state',
                ],
                [
                  <Mono key="8">COMPLETED</Mono>,
                  'Finalized: all twenty gates passed. Terminal.',
                  'Terminal',
                ],
                [<Mono key="9">FAILED</Mono>, 'The migration failed. Terminal.', 'Terminal'],
                [
                  <Mono key="10">ROLLED_BACK</Mono>,
                  'Ownership was returned to the source. Terminal.',
                  'Terminal',
                ],
                [
                  <Mono key="11">SOURCE_IRRECOVERABLE</Mono>,
                  'Destination copy is verified, but the source objects are permanently gone — so finalization can never pass. Recorded as neither success nor failure. Terminal.',
                  'Terminal',
                ],
              ]}
            />

            <Callout tone="note" title="Why SOURCE_IRRECOVERABLE is not just “COMPLETED”">
              <p>
                A migration whose source has been lost has not satisfied the source-retention rule,
                so calling it COMPLETED would be false. It is also not a FAILURE — the data arrived
                intact. It gets its own terminal state, and crucially that state is{' '}
                <strong>not treated as “finished”</strong> by the asset lifecycle, so the affected
                assets stay protected from purge.
              </p>
            </Callout>

            <h3 className="mt-4 text-base font-semibold text-foreground">The seven-step model</h3>
            <Figure
              n={6}
              caption="The operator workflow shown on the migration detail page"
              look="Which step is current, and that Finalize is a separate, explicit action after Ownership — it never happens automatically."
            >
              <svg
                viewBox="0 0 840 90"
                className="h-auto w-full min-w-[680px]"
                role="img"
                aria-label="Seven step workflow: Planned, Approved, Copying, Verified, Ownership, Finalize, Completed."
              >
                {[
                  'Planned',
                  'Approved',
                  'Copying',
                  'Verified',
                  'Ownership',
                  'Finalize',
                  'Completed',
                ].map((s, i) => (
                  <g key={s}>
                    <rect
                      x={10 + i * 118}
                      y="25"
                      width="106"
                      height="40"
                      rx="6"
                      className={cn(
                        'stroke-border',
                        i === 6 ? 'fill-primary/15' : 'fill-background',
                      )}
                      strokeWidth="1.5"
                    />
                    <text
                      x={63 + i * 118}
                      y="50"
                      textAnchor="middle"
                      className="fill-foreground text-[11px]"
                    >
                      {s}
                    </text>
                    {i < 6 && (
                      <line
                        x1={116 + i * 118}
                        y1="45"
                        x2={128 + i * 118}
                        y2="45"
                        className="stroke-muted-foreground"
                        strokeWidth="1.5"
                      />
                    )}
                  </g>
                ))}
              </svg>
            </Figure>

            <h3 className="mt-4 text-base font-semibold text-foreground">
              Reversible vs irreversible
            </h3>
            <Table
              head={['Action', 'Reversible?', 'Notes']}
              rows={[
                ['Plan a migration', 'Yes', 'Creates a record. Nothing is copied.'],
                ['Dry run', 'Yes', 'Read-only rehearsal. Changes nothing.'],
                ['Approve', 'Yes', 'Marks the plan approved. Still copies nothing.'],
                [
                  'Run (copy)',
                  'Yes in effect',
                  'Writes objects to the target. The source is untouched, so the copy can be discarded.',
                ],
                [
                  'Ownership flip',
                  <strong key="r">Yes — an explicit rollback exists</strong>,
                  'Points the catalogue at the target. Ownership rollback returns it to the source.',
                ],
                [
                  'Finalize',
                  <strong key="n">No</strong>,
                  'Terminal. Only permitted when all twenty gates pass.',
                ],
                [
                  'Disposition as SOURCE_IRRECOVERABLE',
                  <strong key="n2">No</strong>,
                  'Terminal, permanent and attributed to the operator. Requires a written justification.',
                ],
              ]}
            />
          </Section>

          {/* 13 ----------------------------------------------------- */}
          <Section id="pre-migration" n={13} title="Pre-migration checklist">
            <p>Complete every line before you plan a migration.</p>
            <Checklist
              items={[
                'S3 bucket exists and is listed in the AWS Console',
                'Bucket Region recorded, and it matches the Region field in Service Portal exactly',
                'Block all public access is ON (all four sub-options)',
                'Default encryption is enabled (SSE-S3, or SSE-KMS with kms:Decrypt and kms:GenerateDataKey also granted)',
                'Bucket Versioning enabled (recommended)',
                'Object Lock is disabled, and no S3 lifecycle expiration rule exists on the bucket',
                'IAM policy created with exactly the four actions, correctly split across bucket and object ARNs',
                'Policy is attached to the dedicated IAM user, confirmed on the user’s Permissions tab',
                'Access key stored in a secret manager, never committed and never pasted into a ticket',
                'Service Portal S3 provider configured — all four required fields, Custom Endpoint left blank',
                'Connection test passes (remembering it only proves ListBucket)',
                'Source provider is healthy and reachable',
                'Database and file backup completed and verified',
                'Dry run completed with no blocking findings',
                'You have platform:system:manage permission',
                'You understand that the source is retained, and that finalize and disposition are irreversible',
                'A maintenance window is agreed and someone is watching the migration',
              ]}
            />
          </Section>

          {/* 14 ----------------------------------------------------- */}
          <Section id="dry-run" n={14} title="Dry run & preflight">
            <p>
              The dry run is a rehearsal. It evaluates the migration and{' '}
              <strong>writes nothing</strong> — no object is copied, no ownership moves, no status
              is advanced beyond the dry-run states.
            </p>

            <Table
              head={['Checked', 'Not changed']}
              rows={[
                [
                  'That the plan resolves: source and target providers, drivers registered, item set and object count',
                  'No object is written to the target',
                ],
                [
                  'That no other non-terminal migration is in flight for the same scope',
                  'No catalogue ownership changes',
                ],
                [
                  'That every item is in a consistent recorded state before work begins',
                  'No asset row is modified',
                ],
                [
                  'That protected assets are not caught up in the migration',
                  'Nothing is deleted anywhere, ever',
                ],
              ]}
            />

            <p>
              Permissions used during a dry run are the same four as normal operation. Running a dry
              run needs <Mono>platform:system:manage</Mono>; viewing results needs{' '}
              <Mono>platform:system:view</Mono>.
            </p>

            <Callout tone="warning" title="Do not proceed just because some checks passed">
              <p>
                A partially green dry run is a stop signal, not a nudge. Resolve every finding
                first: fix the IAM policy, correct the provider configuration, or repair the
                inconsistent asset — then re-run the dry run and require a clean result. Approving a
                migration over a known finding turns a five-minute fix into a mid-copy failure.
              </p>
            </Callout>
          </Section>

          {/* 15 ----------------------------------------------------- */}
          <Section id="execute" n={15} title="Executing the migration">
            <ol className="ml-5 list-decimal space-y-2">
              <li>
                <strong>Confirm the source.</strong> Open the migration and check the source
                provider is the one currently owning the assets.
              </li>
              <li>
                <strong>Confirm the target.</strong> It must be your new Amazon S3 provider, with a
                passing connection test.
              </li>
              <li>
                <strong>Confirm the dry run.</strong> Status <Mono>DRY_RUN_COMPLETE</Mono> with no
                outstanding findings.
              </li>
              <li>
                <strong>Confirm the backup.</strong> Database and files, taken and verified.
              </li>
              <li>
                <strong>Approve.</strong> This records the approval and moves the migration to{' '}
                <Mono>READY</Mono>. Nothing has been copied yet.
              </li>
              <li>
                <strong>Run.</strong> Status becomes <Mono>RUNNING</Mono> and objects are copied to
                S3. The source is left completely untouched.
              </li>
              <li>
                <strong>Monitor.</strong> Watch progress and the per-item states. Copying large
                video and document assets takes time; a long-running migration is not a stuck one.
                Pause if you must stop — do not tear down the service mid-copy.
              </li>
              <li>
                <strong>
                  Reach <Mono>VERIFYING</Mono>.
                </strong>{' '}
                Every item copied. This is a stable resting point; nothing further happens without
                you.
              </li>
              <li>
                <strong>Preview ownership</strong>, then <strong>flip ownership</strong> so the
                catalogue points at S3. This has an explicit rollback if you need it.
              </li>
              <li>
                <strong>Preview finalization.</strong> This runs all twenty gates read-only and
                reports exactly which, if any, refuse.
              </li>
              <li>
                <strong>Finalize</strong> — only when the preview is clean. The engine re-runs every
                gate and refuses at the first failure, so a finalize cannot be forced.
              </li>
            </ol>

            <Callout tone="warning" title="Finalize is irreversible">
              <p>
                There is no un-finalize. Run the finalization preview first, every time. If a gate
                refuses, the correct response is to fix the underlying condition — never to look for
                a way around the gate.
              </p>
            </Callout>

            <h3 className="mt-4 text-base font-semibold text-foreground">
              The twenty finalization gates
            </h3>
            <p>
              The engine refuses at the first gate that fails, naming it. These are the gates, in
              evaluation order:
            </p>
            <Table
              head={['#', 'Gate', 'Refuses when']}
              rows={[
                [
                  '1',
                  <Mono key="g">UNEXPECTED_MIGRATION_COUNT</Mono>,
                  'Another non-terminal migration exists',
                ],
                [
                  '2',
                  <Mono key="g">MIGRATION_NOT_FOUND</Mono>,
                  'The migration record cannot be resolved',
                ],
                ['3', <Mono key="g">STATUS_NOT_VERIFYING</Mono>, 'Status is not VERIFYING'],
                ['4', <Mono key="g">NOT_APPROVED</Mono>, 'The dry run was never approved'],
                [
                  '5',
                  <Mono key="g">TOTAL_OBJECTS_MISMATCH</Mono>,
                  'Recorded object count ≠ actual item count',
                ],
                [
                  '6',
                  <Mono key="g">ITEM_COUNT_MISMATCH</Mono>,
                  'There are no items, or the count disagrees',
                ],
                ['7', <Mono key="g">ITEM_NOT_COMPLETED</Mono>, 'Any item is not COMPLETED'],
                ['8', <Mono key="g">ITEM_NOT_VERIFIED</Mono>, 'Any item is not marked verified'],
                [
                  '9',
                  <Mono key="g">ITEM_CHECKSUM_MISSING</Mono>,
                  'An item has no recorded checksum',
                ],
                [
                  '10',
                  <Mono key="g">ITEM_CHECKSUM_UNEQUAL</Mono>,
                  'Source and target checksums disagree',
                ],
                [
                  '11',
                  <Mono key="g">TARGET_OBJECT_MISSING</Mono>,
                  'A target object is absent from S3',
                ],
                [
                  '12',
                  <Mono key="g">TARGET_CHECKSUM_MISMATCH</Mono>,
                  'A re-downloaded target object does not hash to its recorded checksum',
                ],
                [
                  '13',
                  <Mono key="g">ASSET_NOT_ON_TARGET_PROVIDER</Mono>,
                  'An asset is not owned by the target — ownership was not flipped',
                ],
                ['14', <Mono key="g">ASSET_NOT_ACTIVE</Mono>, 'An asset is not ACTIVE'],
                [
                  '15',
                  <Mono key="g">PROTECTED_ASSET_IN_MIGRATION</Mono>,
                  'A protected asset was included in the items',
                ],
                [
                  '16',
                  <Mono key="g">PROTECTED_ASSET_ON_TARGET</Mono>,
                  'A protected asset’s object leaked onto the destination',
                ],
                [
                  '17',
                  <Mono key="g">SOURCE_OBJECT_MISSING</Mono>,
                  'A SOURCE object is gone — source retention was violated',
                ],
                [
                  '18',
                  <Mono key="g">STATUS_WRITE_ROW_COUNT_NOT_ONE</Mono>,
                  'The status changed underneath the operation',
                ],
                [
                  '19',
                  <Mono key="g">UNSUPPORTED_DRIVER</Mono>,
                  'Source or target driver is not registered',
                ],
                [
                  '20',
                  <Mono key="g">DIVERGENT_ASSET_VERSION</Mono>,
                  'An asset version row diverges',
                ],
              ]}
            />
            <Callout tone="note" title="Gate 17 is the one people misread">
              <p>
                <Mono>SOURCE_OBJECT_MISSING</Mono> checks the <strong>source</strong>, not the
                target. It is how the engine enforces that a migration retained its original copy.
                If it refuses, restoring files to the <em>target</em> will not help — the gate is
                asking about the old provider.
              </p>
            </Callout>
          </Section>

          {/* 16 ----------------------------------------------------- */}
          <Section id="verification" n={16} title="Post-migration verification">
            <Table
              head={['Check', 'PASS', 'FAIL']}
              rows={[
                [
                  'Migration status',
                  <span key="1">
                    <Mono>COMPLETED</Mono>
                  </span>,
                  'Any other status, or a finalize that refused at a gate',
                ],
                [
                  'Migration item states',
                  'Every item COMPLETED and verified',
                  'Any item not COMPLETED, or unverified',
                ],
                [
                  'Object count',
                  'Provider usage on the S3 card matches the migrated item count',
                  'Fewer objects than items',
                ],
                [
                  'Checksums',
                  'Gates 10 and 12 passed — items agree and targets re-hash correctly',
                  'Any checksum mismatch — stop and investigate, do not retry blindly',
                ],
                [
                  'Provider ownership',
                  'Assets report the S3 provider as owner (gate 13)',
                  'Assets still owned by the source',
                ],
                [
                  'Download',
                  'An asset downloads intact and opens correctly',
                  'Download fails — check s3:GetObject',
                ],
                [
                  'Signed URL',
                  'A generated signed URL opens the file in a browser',
                  'URL 403s — check s3:GetObject and that the bucket is not blocking via policy',
                ],
                [
                  'Thumbnail / preview',
                  'Previews render in the asset explorer',
                  'Broken previews — usually the thumbnail object did not migrate',
                ],
                [
                  'Audit record',
                  'The finalization is recorded in the platform audit log',
                  'No audit entry',
                ],
                [
                  'Storage operations',
                  'New operations recorded against the S3 provider with no failures',
                  'Failures with AccessDenied — the object policy is wrong',
                ],
                [
                  'Source retained',
                  'Source objects still present (gate 17 passed)',
                  'Source objects missing — see the note below',
                ],
                [
                  'No unexpected deletions',
                  'Asset counts unchanged; nothing was removed',
                  'Any drop in asset count',
                ],
              ]}
            />

            <Callout
              tone="warning"
              title="Never delete the source just because the migration looks fine"
            >
              <p>
                Source objects are retained deliberately, and the asset lifecycle — not you, and not
                an S3 lifecycle rule — decides when anything may be removed. Deleting the source
                manually destroys the rollback path and will cause gate 17 to refuse finalization if
                you have not finalized yet.
              </p>
              <p>
                If the source genuinely no longer exists, the migration cannot be finalized. That
                situation has a dedicated governed path, ending in the terminal state{' '}
                <Mono>SOURCE_IRRECOVERABLE</Mono>, which requires a written justification and an
                explicit operator attestation, and which deliberately keeps the affected assets
                protected from lifecycle purge.
              </p>
            </Callout>
          </Section>

          {/* 17 ----------------------------------------------------- */}
          <Section id="security" n={17} title="Security best practices">
            <ul className="ml-5 list-disc space-y-2">
              <li>
                <strong>Least privilege.</strong> Four actions. Never <Mono>s3:*</Mono>, never{' '}
                <Mono>AmazonS3FullAccess</Mono>.
              </li>
              <li>
                <strong>Private bucket, always.</strong> Block all public access stays on. Signed
                URLs are the access path.
              </li>
              <li>
                <strong>Encryption at rest.</strong> Leave default encryption enabled. With KMS,
                remember the extra key permissions.
              </li>
              <li>
                <strong>Dedicated identity per environment.</strong> Production and staging never
                share a key.
              </li>
              <li>
                <strong>Secret hygiene.</strong> Never in source code, never in frontend code, never
                in a committed <Mono>.env</Mono>, never in a screenshot, log, report or support
                ticket.
              </li>
              <li>
                <strong>Rotate on a schedule.</strong> Create the new key, update Service Portal,
                connection-test, then delete the old key — in that order, so there is no outage.
              </li>
              <li>
                <strong>Separate policies for separate jobs.</strong> Recovery or version-inspection
                permissions belong in their own temporary policy, attached only for the duration and
                detached afterwards.
              </li>
              <li>
                <strong>Never widen IAM to clear an error.</strong> Diagnose which specific action
                and ARN was denied, and grant precisely that.
              </li>
              <li>
                <strong>Never make the bucket public to fix AccessDenied.</strong> It exposes
                customer files and does not address the cause.
              </li>
              <li>
                <strong>
                  Never delete source data on the strength of a migration “looking” successful.
                </strong>{' '}
                Let the lifecycle rules decide.
              </li>
            </ul>
          </Section>

          {/* 18 ----------------------------------------------------- */}
          <Section id="troubleshooting" n={18} title="Troubleshooting">
            <Table
              head={['Symptom', 'Likely cause', 'How to check', 'How to fix']}
              rows={[
                [
                  <Mono key="1">AccessDenied</Mono>,
                  'Missing s3:ListBucket, or the policy is not attached',
                  'IAM → user → Permissions tab; re-read the Resource ARNs',
                  'Attach the policy; put ListBucket on the bucket ARN without /*',
                ],
                [
                  <Mono key="2">InvalidAccessKeyId</Mono>,
                  'Access Key ID typo, or key deleted',
                  'Compare with IAM → Security credentials',
                  'Re-enter, or create a new key pair',
                ],
                [
                  <Mono key="3">SignatureDoesNotMatch</Mono>,
                  'Secret truncated or has trailing whitespace',
                  'Re-copy from the secret manager',
                  'Re-enter the secret; create a new key if it was lost',
                ],
                [
                  <Mono key="4">NoSuchBucket</Mono>,
                  'Wrong bucket name, or ARN/scheme pasted into the field',
                  'S3 → Buckets, compare exactly',
                  'Enter the bare bucket name only',
                ],
                [
                  'Wrong region / PermanentRedirect',
                  'Region field does not match the bucket',
                  'S3 → Buckets → AWS Region column',
                  'Correct the Region field; the error often names the right one',
                ],
                [
                  'Bucket exists but the test fails',
                  'ListBucket denied, or a custom endpoint is set',
                  'Read the message after “Provider error [driver=s3]”',
                  'Fix ListBucket scoping; clear Custom Endpoint for AWS',
                ],
                [
                  'Test passes, uploads fail',
                  'Object-level actions missing — the test only proves ListBucket',
                  'Check the second statement covers the /* ARN',
                  'Grant GetObject, PutObject, DeleteObject on the object ARN',
                ],
                [
                  'GetObject denied',
                  'Object statement missing GetObject, or KMS key permission missing',
                  'Read the error — it names S3 or KMS',
                  'Add the action, or add kms:Decrypt on the key',
                ],
                [
                  'PutObject denied',
                  'PutObject missing, or an Object Lock retention rule',
                  'Bucket → Properties → Object Lock',
                  'Grant PutObject; disable Object Lock',
                ],
                [
                  'DeleteObject denied',
                  'DeleteObject missing, or Object Lock',
                  'Same as above',
                  'Grant DeleteObject; disable Object Lock',
                ],
                [
                  'Signed URL returns 403',
                  'Object missing, GetObject denied, or the URL expired',
                  'Confirm the object exists; regenerate the URL',
                  'Fix GetObject; regenerate rather than reusing an old link',
                ],
                [
                  'Migration appears stuck',
                  'Large objects still copying; or the source provider is unreachable',
                  'Check per-item states and the source provider health',
                  'Allow time; if truly stalled, pause rather than tearing anything down',
                ],
                [
                  'Finalization refuses at a gate',
                  'A real precondition is unmet — the gate is correct',
                  'The refusal names the exact gate',
                  'Fix the underlying condition. Never weaken or bypass a gate.',
                ],
                [
                  <Mono key="5">TARGET_CHECKSUM_MISMATCH</Mono>,
                  'A copied object does not match its recorded hash',
                  'Gate 12 names the asset',
                  'Stop. Investigate that object before doing anything else — do not re-run blindly.',
                ],
                [
                  <Mono key="6">TARGET_OBJECT_MISSING</Mono>,
                  'The object never landed, or was removed after copying',
                  'Gate 11 names the key; check the bucket and any lifecycle rule',
                  'Remove S3 expiry rules; re-run the copy for that item',
                ],
                [
                  <Mono key="7">SOURCE_OBJECT_MISSING</Mono>,
                  'A source object is gone — source retention violated',
                  'Gate 17 names the key. Check the SOURCE provider, not the target',
                  'Restore the source if possible. If it is permanently gone, the governed SOURCE_IRRECOVERABLE disposition is the correct path.',
                ],
                [
                  'Provider shows OFFLINE',
                  'A connection test failed, or the provider is incompletely configured',
                  'The card shows the last health error',
                  'Resolve the error, then re-run the connection test',
                ],
                [
                  'Lifecycle blocker on an asset',
                  'The asset is attached to a migration that is not in a terminal state',
                  'Check the migration status for that asset',
                  'Bring the migration to a terminal state through the proper workflow',
                ],
              ]}
            />
          </Section>

          {/* Footer nav */}
          <div className="border-t border-border pt-6">
            <Link
              to={PLATFORM_ROUTES.STORAGE_PROVIDERS}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
              Back to Storage Providers
            </Link>
            <p className="mt-3 text-xs text-muted-foreground">
              If you opened this guide from the Configure dialog, your original tab is still open
              with your form intact — switch back to it rather than using this link.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
